import { IS_NATIVE, PLATFORM } from "../config.js";
import { loadApp, loadStatusBar, loadSplashScreen } from "./plugins.js";
import { flushStorage } from "./storage.js";

/**
 * Native shell behaviour that has no web equivalent: the splash handoff, the status bar,
 * the Android hardware back button, and persisting state when the OS backgrounds us.
 *
 * All of it is a no-op on web, so main.js calls it unconditionally.
 */

const nextFrame = () => new Promise(requestAnimationFrame);

/** Lifts the held (launchAutoHide: false) native splash once #splash has painted — two frames, since the first rAF precedes its paint. */
export async function hideSplashAfterFirstFrame() {
  if (!IS_NATIVE) return;
  await nextFrame();
  await nextFrame();
  try {
    const { SplashScreen } = await loadSplashScreen();
    await SplashScreen.hide({ fadeOutDuration: 0 });
  } catch (error) {
    console.warn("[platform] splash hide failed:", error);
  }
}

async function setupStatusBar() {
  try {
    const { StatusBar, Style } = await loadStatusBar();
    // Overlay mode is what makes env(safe-area-inset-top) non-zero; styles.css already
    // offsets the corner buttons by it.
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Capacitor's naming is inverted from intuition: Style.Light means DARK glyphs, for a
    // light background. The app's chrome is white/light grey, so Style.Dark rendered the
    // clock and battery in white-on-white and they vanished.
    await StatusBar.setStyle({ style: Style.Light });
    if (PLATFORM === "android") {
      await StatusBar.setBackgroundColor({ color: "#00000000" });
    }
  } catch (error) {
    console.warn("[platform] status bar setup failed:", error);
  }
}

/** Android hardware back. Never a bare history.back(): only the layout picker pushes entries, so elsewhere it would exit. */
async function setupBackButton(app) {
  let armed = false;
  let timer = null;

  const { App } = await loadApp();
  await App.addListener("backButton", () => {
    if (app.getUIManager()?.handleBackNavigation()) {
      armed = false;
      clearTimeout(timer);
      return;
    }

    if (armed) {
      App.exitApp();
      return;
    }

    armed = true;
    const ui = app.getUIManager();
    ui?.showNotification?.(ui.t("designerNotifyPressBackToExit", "Press back again to exit"), "info");
    timer = setTimeout(() => { armed = false; }, 2000);
  });
}

/**
 * Android can kill a backgrounded process before a debounced save lands, so flush on the
 * way out rather than relying on the render loop's visibilitychange pause alone.
 */
async function setupAppState(app) {
  const { App } = await loadApp();
  await App.addListener("appStateChange", async ({ isActive }) => {
    if (isActive) return;
    app.getStateManager()?.saveSceneState();
    // saveSceneState only schedules the mirror; force it, because the process may not
    // survive long enough for a 400ms debounce to fire.
    await flushStorage();
  });
}

/** Wire every native-only behaviour. No-op on web. */
export async function initNativeShell(app) {
  if (!IS_NATIVE) return;
  await Promise.all([
    setupStatusBar(),
    setupBackButton(app),
    setupAppState(app),
  ]).catch((error) => console.warn("[platform] native shell setup failed:", error));
}
